import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";

import { Link as RouterLink, useNavigate } from "react-router-dom";

import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { visuallyHidden } from "@mui/utils";
import { createStyles, makeStyles } from "@mui/styles";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { headCells, rowsPerPageOptions } from "./data";
import JobService from "../../service/job";
import { useSelector } from "react-redux";
import { ConfirmPopup } from "../Common/Common";
import { showToast } from "../../store/actions";

const tableWidths = headCells.map((x) => x.width);

const useStyles = makeStyles((theme) =>
  createStyles({
    tr: {
      "& td, & th": {
        wordBreak: "break-word",
      },
      "&:nth-of-type(odd)": {
        backgroundColor: "#ffffff",

        "& td:first-child": {
          backgroundColor: "#ffffff",
        },
      },
      "&:nth-of-type(even)": {
        "& td:first-child": {
          backgroundColor: "white",
        },
      },
    },
    StatusDrp: {
      "& .MuiSelect-select:focus": {
        backgroundColor: "transparent",
      },
    },
    Header: {
      fontSize: "1.1rem",
      minHeight: "60px !important",
      backgroundColor: "#e8edf1",
      color: "black",
      marginTop: 0,
      borderRadius: 0,
      boxShadow: 0,
      fontWeight: 600,
    },
    TableHead: {
      fontWeight: 600,
      "& span.Mui-active": {
        background: "transparent",
        color: "inherit",
      },
      "& span": {
        "&:hover": {
          background: "transparent",
          color: "rgba(0, 0, 0, 0.6) !important",
        },
      },
    },
  })
);

const statusList = [
  { value: -1, label: "All" },
  { value: 1, label: "Awaiting Assign" },
  { value: 2, label: "Awaiting Order" },
  { value: 3, label: "Awaiting Order Approval" },
  { value: 4, label: "Artwork in process" },
  { value: 5, label: "Awaiting Artwork Approval" },
  { value: 6, label: "Image File in process" },
  { value: 7, label: "Awaiting Image File Approval" },
  { value: 8, label: "In Production" },
  { value: 9, label: "Delivery Underway" },
  { value: 10, label: "Completed" },
];

export default function DataTable() {
  const { user } = useSelector((state) => state);
  const role = user.currentUser?.Role_id;

  /* () -> Denotes some default value for filters
  Admin | Employee | Customer | Supplier  (When different roles login)
   -1   |   ()     |    -1    |   -1      (defaultEmployee)
   -1   |   -1     |    ()    |   -1      (defaultCustomer)
   -1   |   -1     |    -1    |   ()      (defaultSupplier) */
  const defaultEmployee =
    user.currentUser.Role_id === 1 ? -1 : user.currentUser.isAdminOrEmp === 1 ? user.currentUser.User_id : -1;
  const defaultCustomer =
    user.currentUser.isAdminOrEmp === 1 || user.currentUser.Role_id === 4 ? -1 : user.currentUser.User_id;
  const defaultSupplier = user.currentUser.Role_id === 4 ? user.currentUser.User_id : -1;

  const classes = useStyles();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("Job_id");
  const [Job_id, setJob_id] = useState("");
  const [jobId, setJobId] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [tableHeight, setTableHeight] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [status, setStatus] = useState(-1);
  const [category, setCategory] = useState(-1);
  const [customer, setCustomer] = useState(defaultCustomer);
  const [startdate, setStartdate] = useState(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
  const [employee, setEmployee] = useState(defaultEmployee);
  const [supplier, setSupplier] = useState(defaultSupplier);
  const [loadingDeletePopup, setLoadingDeletePopup] = useState(false);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const searchRef = useRef(null);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [filters, setFilters] = useState({
    categories: [{ label: "All", value: -1 }],
    customers: [{ label: "All", value: -1 }],
    employees: [{ label: "All", value: -1 }],
    suppliers: [{ label: "All", value: -1 }],
    status: statusList,
  });

  const resetFilters = () => {
    setJob_id("");
    setLimit(10);
    setPage(0);
    setOrder("desc");
    setOrderBy("Job_id");
    setStatus(-1);
    setCategory(-1);
    setEmployee(defaultEmployee);
    setCustomer(defaultCustomer);
    setStartdate(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
    setSupplier(defaultSupplier);

    loadJobsdata({
      Job_id: "",
      limit: 10,
      page: 0,
      orderBy: "Job_id",
      order: "desc",
      status: -1,
      category: -1,
      employee: defaultEmployee,
      customer: defaultCustomer,
      supplier: defaultSupplier,
      startdate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
    });
  };

  //Loading account List
  const loadJobsdata = async (options) => {
    setLoading(true);

    const jobService = new JobService();
    const result = await jobService.getAllJobs({
      Job_id,
      limit,
      page,
      orderBy,
      order,
      status,
      category,
      employee,
      customer,
      supplier,
      ...options,
      startdate: new Date(options.startdate || startdate).toISOString().slice(0, 19).replace("T", " "),
    });

    if (result.success && result.data && Object.keys(result.data.jobList).length > 0) {
      setData(result.data.jobList);
      setCount(result.data.count);
    } else {
      setData([]);
      setCount(0);
    }

    setLoading(false);
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setPage(0);
      setJob_id(searchRef.current.value);
      loadJobsdata({ page: 0, Job_id: searchRef.current.value });
    }
  };

  const handleClearSearch = () => {
    searchRef.current.value = "";
    setJob_id("");
    setPage(0);
    loadJobsdata({
      Job_id: "",
      page: 0,
    });
  };

  const handleFilter = (key, value) => {
    switch (key) {
      case "status":
        setStatus(value);
        break;

      case "category":
        setCategory(value);
        break;

      case "customer":
        setCustomer(value);
        break;

      case "employee":
        setEmployee(value);
        break;

      case "startdate":
        setStartdate(value);
        break;

      case "supplier":
        setSupplier(value);
        break;

      default:
        break;
    }

    setPage(0);
    loadJobsdata({ [key]: value, page: 0 });
  };

  useEffect(() => {
    (async () => {
      const jobService = new JobService();
      const response = await jobService.getJobFilters();

      if (response.success) {
        const { categories, customers, employees, suppliers } = response.data;

        setFilters((prev) => ({
          ...prev,
          categories: [...prev.categories, ...categories],
          customers: [...prev.customers, ...customers],
          employees: [...prev.employees, ...employees],
          suppliers: [...prev.suppliers, ...suppliers],
        }));
      }
    })();

    return () => {
      setFilters({
        categories: [{ label: "All", value: -1 }],
        customers: [{ label: "All", value: -1 }],
        employees: [{ label: "All", value: -1 }],
        suppliers: [{ label: "All", value: -1 }],
        status: statusList,
      });
    };
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const status = query.get("status");

    if (status) setStatus(status);

    //Load Account list on start
    loadJobsdata({ ...(status ? { status } : {}) });
    return () => setData([]);
  }, []);

  useLayoutEffect(() => {
    function updateSize() {
      setTableHeight(window.innerHeight - 230);
    }
    window.addEventListener("resize", updateSize);
    updateSize();

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const handleRequestSort = (_event, property) => {
    const isAsc = orderBy === property && order === "asc";
    let tmpOrder = isAsc ? "desc" : "asc";

    setOrder(tmpOrder);
    setOrderBy(property);
    loadJobsdata({ orderBy: property, order: tmpOrder });
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    loadJobsdata({
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
    loadJobsdata({
      limit: parseInt(event.target.value, 10),
      page: 0,
    });
  };

  // Function for Delete Job
  const deleteJob = async (jobId) => {
    setLoadingDeletePopup(true);

    const jobService = new JobService();
    const result = await jobService.deleteJob(jobId);

    if (result.success) {
      loadJobsdata({});

      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
    }

    setOpenDeletePopup(false);
    setLoadingDeletePopup(false);
  };

  function DataTableHead(props) {
    const { order, orderBy, onRequestSort, hidden, classes } = props;
    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };

    return (
      <>
        <TableHead style={hidden ? { visibility: "collapse" } : {}}>
          <TableRow>
            {headCells
              .filter(
                (column) =>
                  (defaultCustomer === -1 ||
                    (defaultCustomer !== -1 && !["CustomerName", "EmployeeName"].includes(column.id))) &&
                  (defaultEmployee === -1 || (defaultEmployee !== -1 && !["Actions"].includes(column.id))) &&
                  (defaultEmployee !== -1 || !["SupplierName"].includes(column.id)) &&
                  (defaultSupplier === -1 || !["CustomerName", "Actions"].includes(column.id))
              )
              .map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? "right" : "left"}
                  padding={headCell.disablePadding ? "none" : "normal"}
                  sortDirection={orderBy === headCell.id && !headCell.sortable ? order : false}
                  style={{
                    width: headCell.width,
                    ...(headCell.id === "Job_id" && {
                      position: "sticky",
                      left: 0,
                      zIndex: 900,
                    }),
                  }}
                  className={classes.TableHead}
                >
                  {!headCell.sortable ? (
                    headCell.label
                  ) : (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : "asc"}
                      onClick={createSortHandler(headCell.id)}
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
          </TableRow>
        </TableHead>
      </>
    );
  }

  DataTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
  };

  const DataTableToolbar = () => {
    return (
      <Toolbar
        className={classes.Header}
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        }}
      >
        <TextField
          margin="normal"
          inputRef={searchRef}
          onKeyPress={handleSearch}
          placeholder="Job id"
          variant="standard"
          InputProps={{
            startAdornment: <SearchIcon color="primary" />,
            ...(searchRef?.current?.value && {
              endAdornment: (
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon color="primary" />
                </IconButton>
              ),
            }),
          }}
          sx={{ mr: "20px" }}
        />

        <TextField
          margin="normal"
          label="Category"
          variant="standard"
          value={category}
          onChange={(e) => handleFilter("category", e.target.value)}
          select
          sx={{ mr: "20px", mt: 0, width: "200px" }}
          className={classes.StatusDrp}
        >
          {filters.categories.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {defaultCustomer === -1 && (
          <TextField
            margin="normal"
            label="Customer"
            variant="standard"
            value={customer}
            onChange={(e) => handleFilter("customer", e.target.value)}
            select
            sx={{ mr: "20px", mt: 0, width: "200px" }}
            className={classes.StatusDrp}
          >
            {filters.customers.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Display Supplier filter only for Employee role */}
        {role === 2 && (
          <TextField
            margin="normal"
            label="Supplier"
            variant="standard"
            value={supplier}
            onChange={(e) => handleFilter("supplier", e.target.value)}
            select
            sx={{ mr: "20px", mt: 0, width: "200px" }}
            className={classes.StatusDrp}
          >
            {filters.suppliers.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startdate}
            onChange={(startdate) => handleFilter("startdate", startdate)}
            renderInput={(params) => (
              <TextField margin="normal" sx={{ mr: "20px", mt: 0, width: "220px" }} variant="standard" {...params} />
            )}
          />
        </LocalizationProvider>

        {user.currentUser?.Role_id === 1 && (
          <TextField
            margin="normal"
            label="Employee"
            variant="standard"
            value={employee}
            onChange={(e) => handleFilter("employee", e.target.value)}
            select
            sx={{ mr: "20px", mt: 0, width: "200px" }}
            className={classes.StatusDrp}
          >
            {filters.employees.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          margin="normal"
          label="Status"
          variant="standard"
          value={status}
          onChange={(e) => handleFilter("status", e.target.value)}
          select
          sx={{ mr: "20px", mt: 0, width: "200px" }}
          className={classes.StatusDrp}
        >
          {statusList.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <IconButton onClick={resetFilters} className="btn btn-icon btn-dark">
          <RefreshIcon />
        </IconButton>

        {/* Display "Start New Job" button only for Admin and Customer */}
        {(role === 1 || role === 3) && (
          <Box style={{ flex: "1 0 auto", textAlign: "right", marginLeft: "20px" }}>
            <Button
              className="btn btn-primary"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(role === 3 ? "/new-job" : "/admin/new-job")}
            >
              Start New Job
            </Button>
          </Box>
        )}
      </Toolbar>
    );
  };

  // Open Job Delete Confirm Popup
  const DeletePopup = ({ jobId }) => {
    return (
      <ConfirmPopup
        configData={{
          popupTitle: "Delete Job",
          popupMessage: "Are you sure you want to delete?",
          handleCancel: handleDeletePopupCancel,
          handleClick: () => deleteJob(jobId),
          loadingForConfirm: loadingDeletePopup,
        }}
      />
    );
  };

  const handleDeletePopupCancel = () => {
    setOpenDeletePopup(false);
  };

  return (
    <Box>
      <AppBar
        sx={{
          position: "relative",
          boxShadow: 0,
          marginTop: "15px",
          backgroundColor: "#e8edf1",
        }}
      >
        {DataTableToolbar()}
      </AppBar>
      <Paper sx={{ width: "100%", mb: 2 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: `${tableHeight}px`,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            sx={{
              maxHeight: `${tableHeight}px`,
              ...(data.length === 0
                ? {
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            <Table stickyHeader sx={{ width: "100%" }} size="small">
              <DataTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={data.length}
                classes={classes}
              />
              {data.length ? (
                <TableBody>
                  {data.map((row, i) => {
                    return (
                      <TableRow tabIndex={-1} key={row.Job_id} className={classes.tr}>
                        <TableCell
                          style={{
                            position: "sticky",
                            left: 0,
                            zIndex: 800,
                            minWidth: tableWidths[0],
                          }}
                          className={classes.td}
                        >
                          <Button
                            color="info"
                            size="small"
                            component={RouterLink}
                            style={{
                              paddingTop: 0,
                              paddingBottom: 0,
                              display: "inline-block",
                              wordBreak: "break-word",
                              verticalAlign: "middle",
                            }}
                            to={`./${row.Job_id}`}
                          >
                            PO {row.ProductLink.startsWith("#") ? row.ProductLink : "#" + row.ProductLink}
                          </Button>
                        </TableCell>
                        {/* Display "Customer Name" column only for Admin, Employee */}
                        {user.currentUser.isAdminOrEmp === 1 && (
                          <TableCell style={{ minWidth: tableWidths[1] }} component="th" scope="row" padding="normal">
                            {row.CustomerName}
                          </TableCell>
                        )}
                        <TableCell style={{ minWidth: tableWidths[2] }} component="th" scope="row" padding="normal">
                          {row.CategoryName}
                        </TableCell>
                        <TableCell style={{ minWidth: tableWidths[3] }}>{row.Name}</TableCell>
                        <TableCell style={{ minWidth: tableWidths[4] }}>$ {row.Amount}</TableCell>
                        {/* Display "Employee Name" column only for Admin, Employee and Supplier */}
                        {defaultCustomer === -1 && (
                          <TableCell style={{ minWidth: tableWidths[5] }}>{row.EmployeeName}</TableCell>
                        )}
                        {defaultEmployee !== -1 && (
                          <TableCell style={{ minWidth: tableWidths[6] }}>{row.SupplierName}</TableCell>
                        )}
                        <TableCell style={{ minWidth: tableWidths[7] }}>{row.StatusText}</TableCell>
                        <TableCell style={{ minWidth: tableWidths[8] }} component="th" scope="row" padding="normal">
                          {row.CreatedAt}
                        </TableCell>
                        {/* Only when Admin or Customer logins */}
                        {defaultEmployee === -1 && role !== 4 && (
                          <TableCell style={{ minWidth: tableWidths[9] }}>
                            {/* Display Edit button only for Customer and when Status is 1 */}
                            {defaultCustomer !== -1 && row.Status === "1" && (
                              <Tooltip
                                title="Edit Job"
                                className="btn btn-icon btn-success-light"
                                style={{ verticalAlign: "middle" }}
                              >
                                <IconButton
                                  color="primary"
                                  onClick={() =>
                                    navigate("/add-job", {
                                      state: {
                                        Category_id: row.Category_id,
                                        CategoryName: row.CategoryName,
                                        SubCategory_id: row.SubCategory_id,
                                        SubCategoryName: row.SubcategoryName,
                                        JobId: row.Job_id,
                                        CategoryProductLink: row.CategoryProductLink,
                                      },
                                    })
                                  }
                                >
                                  <ModeEditIcon />
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* Display Delete button always for Admin and only for Customer when Status is 1 */}
                            {(defaultCustomer === -1 || (defaultCustomer !== -1 && row.Status === "1")) && (
                              <Tooltip
                                title="Delete Job"
                                className="btn btn-icon btn-danger-light"
                                style={{ verticalAlign: "middle" }}
                              >
                                <IconButton
                                  color="error"
                                  onClick={() => {
                                    setJobId(row.Job_id);
                                    setOpenDeletePopup(true);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              ) : (
                <TableBody></TableBody>
              )}
            </Table>
          </TableContainer>
        )}
        <>
          {!data.length && !loading && (
            <div
              style={{
                height: tableHeight - 64, //64 for the table title space
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              No Data Available
            </div>
          )}
        </>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={count}
          rowsPerPage={limit}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      {openDeletePopup && <DeletePopup jobId={jobId} />}
    </Box>
  );
}
